import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, Link } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import './styles/App.css';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ToastProvider } from './contexts/ToastContext';
import Login from './components/auth/Login';
import Profile, { Avatar } from './components/auth/Profile';
import ProtectedRoute from './components/auth/ProtectedRoute';
import Calendar from './components/booking/Calendar';
import ChatBot from './components/common/ChatBot';
import AdminPanel from './components/admin/AdminPanel';
import ManagerPanel from './components/admin/ManagerPanel';
import MyReservations from './components/booking/MyReservations';
import ReservationDetails from './components/booking/ReservationDetails';
import EmailAdminLayout from './components/admin/email/EmailAdminLayout';
import EmailTemplateList from './components/admin/email/EmailTemplateList';
import EmailTemplateEditor from './components/admin/email/EmailTemplateEditor';
import EmailRuleList from './components/admin/email/EmailRuleList';
import EmailRuleEditor from './components/admin/email/EmailRuleEditor';
import EmailQueuePage from './components/admin/email/EmailQueuePage';
import EmailLogsPage from './components/admin/email/EmailLogsPage';
import QuickRenderPage from './components/admin/email/QuickRenderPage';
import ReservationDetail from './components/admin/email/ReservationDetail';
import ComposePage from './components/admin/email/ComposePage';
import CampaignList from './pages/CampaignList';
import CampaignEditor from './pages/CampaignEditor';
import CampaignStats from './pages/CampaignStats';
import CustomerPreferences from './pages/CustomerPreferences';
import UserGroups from './components/groups/UserGroups';
import Gallery from './components/gallery/Gallery';
import MyPeople from './components/people/MyPeople';
import CalendarEmailSchedule from './components/admin/CalendarEmailSchedule';
import VacancyEmailSchedule from './components/admin/VacancyEmailSchedule';
import ErrorBoundary from './components/common/ErrorBoundary';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30000,
      retry: 1,
    },
  },
});

function AppContent() {
  const { user, logout, isAdmin, isManager, isImpersonating, stopImpersonating } = useAuth();

  const handleLogout = async () => {
    queryClient.clear();
    await logout();
  };

  const handleStopImpersonating = async () => {
    const result = await stopImpersonating();
    if (result.success) {
      window.location.reload();
    }
  };

  return (
    <div className="App">
      {isImpersonating && (
        <div className="impersonation-banner">
          <span>Viewing as: <strong>{user?.name || user?.email}</strong></span>
          <button onClick={handleStopImpersonating} className="btn btn-sm">
            Return to Admin
          </button>
        </div>
      )}
      {user && (
        <nav className="navbar">
          <div className="logo-container">
            <img src="/papas_palace_sign.jpg" alt="Papa's Palace" className="logo-image" />
            <h1 className="logo">Papa's Palace</h1>
          </div>
          <div className="nav-center">
            <Link to="/" className="nav-link">Book</Link>
            <Link to="/my-reservations" className="nav-link">My Reservations</Link>
            <Link to="/groups" className="nav-link">My Groups</Link>
            <Link to="/people" className="nav-link">My People</Link>
            <Link to="/gallery" className="nav-link">Gallery</Link>
            {isManager() && !isAdmin() && (
              <Link to="/manager" className="nav-link">Manager</Link>
            )}
            {isAdmin() && (
              <Link to="/admin" className="nav-link">Admin</Link>
            )}
          </div>
          <div className="nav-right">
            <Link to="/profile" className="user-info-link">
              <Avatar name={user.name} size="small" />
              <span>{user.name}</span>
            </Link>
            <button onClick={handleLogout} className="logout-btn">
              Logout
            </button>
          </div>
        </nav>
      )}

      <main className="container">
        <ErrorBoundary>
        <Routes>
          <Route path="/login" element={
            user ? <Navigate to="/" replace /> : <Login />
          } />

          <Route path="/" element={
            <ProtectedRoute>
              <div className="guest-section">
                <h2>Reserve Your Stay</h2>
                <div className="guest-layout">
                  <Calendar />
                </div>
              </div>
            </ProtectedRoute>
          } />

          <Route path="/my-reservations" element={
            <ProtectedRoute>
              <MyReservations />
            </ProtectedRoute>
          } />

          <Route path="/reservations/:id" element={
            <ProtectedRoute>
              <ReservationDetails />
            </ProtectedRoute>
          } />

          <Route path="/groups" element={
            <ProtectedRoute>
              <UserGroups />
            </ProtectedRoute>
          } />

          <Route path="/people" element={
            <ProtectedRoute>
              <MyPeople />
            </ProtectedRoute>
          } />

          <Route path="/gallery" element={
            <ProtectedRoute>
              <Gallery />
            </ProtectedRoute>
          } />

          <Route path="/profile" element={
            <ProtectedRoute>
              <Profile />
            </ProtectedRoute>
          } />

          <Route path="/manager" element={
            <ProtectedRoute requireManager={true}>
              <div className="manager-section">
                <ManagerPanel />
              </div>
            </ProtectedRoute>
          } />

          <Route path="/admin" element={
            <ProtectedRoute requireAdmin={true}>
              <div className="admin-section">
                <AdminPanel />
              </div>
            </ProtectedRoute>
          } />

          <Route path="/admin/calendar-email" element={
            <ProtectedRoute requireAdmin={true}>
              <div className="admin-section">
                <CalendarEmailSchedule />
              </div>
            </ProtectedRoute>
          } />

          <Route path="/admin/vacancy-email" element={
            <ProtectedRoute requireAdmin={true}>
              <div className="admin-section">
                <VacancyEmailSchedule />
              </div>
            </ProtectedRoute>
          } />

          <Route path="/admin/email" element={
            <ProtectedRoute requireAdmin={true}>
              <div className="admin-section">
                <EmailAdminLayout />
              </div>
            </ProtectedRoute>
          }>
            <Route index element={<Navigate to="templates" replace />} />
            <Route path="templates" element={<EmailTemplateList />} />
            <Route path="templates/new" element={<EmailTemplateEditor />} />
            <Route path="templates/:id" element={<EmailTemplateEditor />} />
            <Route path="render" element={<QuickRenderPage />} />
            <Route path="rules" element={<EmailRuleList />} />
            <Route path="rules/new" element={<EmailRuleEditor />} />
            <Route path="rules/:id" element={<EmailRuleEditor />} />
            <Route path="queue" element={<EmailQueuePage />} />
            <Route path="logs" element={<EmailLogsPage />} />
            <Route path="compose" element={<ComposePage />} />
            <Route path="reservations/:reservationId" element={<ReservationDetail />} />
          </Route>

          {/* Campaign routes - standalone, not nested under email layout */}
          <Route path="/campaigns" element={
            <ProtectedRoute requireAdmin={true}>
              <CampaignList />
            </ProtectedRoute>
          } />
          <Route path="/campaigns/new" element={
            <ProtectedRoute requireAdmin={true}>
              <CampaignEditor />
            </ProtectedRoute>
          } />
          <Route path="/campaigns/:id" element={
            <ProtectedRoute requireAdmin={true}>
              <CampaignStats />
            </ProtectedRoute>
          } />
          <Route path="/campaigns/:id/edit" element={
            <ProtectedRoute requireAdmin={true}>
              <CampaignEditor />
            </ProtectedRoute>
          } />

          {/* Self-serve: your own email preferences, no id needed */}
          <Route path="/preferences" element={
            <ProtectedRoute>
              <CustomerPreferences />
            </ProtectedRoute>
          } />

          {/* Customer preferences route */}
          <Route path="/customers/:customerId/preferences" element={
            <ProtectedRoute requireAdmin={true}>
              <CustomerPreferences />
            </ProtectedRoute>
          } />

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        </ErrorBoundary>
      </main>

      {user && <ChatBot />}
    </div>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ToastProvider>
        <Router>
          <AuthProvider>
            <AppContent />
          </AuthProvider>
        </Router>
      </ToastProvider>
    </QueryClientProvider>
  );
}

export default App;
